import {
  type GenericTemplate,
  type Image,
  type LocationTemplate,
  type MessageContext,
  type ReactionSummary,
  type Promise as RocketchatPromiseType,
  type SystemMessage,
  TemplateType,
  Visible,
} from "@daangn/idl/rocketchat/api/model";
import { Entity } from "@data-client/endpoint";
import type { Values } from "@karrotmarket-com/type-utility";
import type { ChannelID, ChatMessageID, UserID } from "../types";
import { User } from "../user/user.entity";
import { getMessagePrimaryKey } from "./getMessagePrimaryKey";

export const ChatMessageType = {
  ANNO: "ANNO",
  ARTI: "ARTI",
  GALL: "GALL",
  IMAG: "IMAG",
  STIC: "STIC",
  SYST: "SYST",
  TEMP: "TEMP",
  TEXT: "TEXT",
  UNSPECIFIED: "XXXX",
} as const;

export const ResendStatus = {
  RESEND_FAILED: "RESEND_FAILED",
  RESEND_REQUIRED: "RESEND_REQUIRED",
  RESEND_SUCCESS: "RESEND_SUCCESS",
  UNSPECIFIED: "UNSPECIFIED",
} as const;

export const TemporaryStatus = {
  NOT_TEMPORARY: "NOT_TEMPORARY",
  TEMPORARY: "TEMPORARY",
} as const;

export class ChatMessage extends Entity {
  /**
   * ID
   * 서버에서 자동으로 생성된다. 클라이언트에서 값을 넣어서 보내면 안된다.
   * 1부터 시작하고 새로운 메시지가 생성될 때마다 1씩 증가된다.
   * 모든 message는 1이상의 값을 가지고 있다.
   */
  id: ChatMessageID = "";
  /**
   * TEXT: 텍스트, STIC: 스티커, SYST: 시스템, IMAG: 이미지, GALL: 갤러리,
   * ARTI: 게시글, TEMP: 템플릿(template_type 추가 설정 필요), ANNO: 공지
   * XXXX: 미지원 타입
   * (Deprecated types: COUP, PROM)
   */
  type = "";
  /** Message를 보내는 곳의 channel id. */
  channel_id: ChannelID = "";

  normalizeKey = "";

  /** 대화 내용 */
  content = "";
  /** Message가 생성된 시간. 서버에서 생성한다. */
  create_time?: string | undefined = undefined;
  /**
   * Message를 본 사용자의 map.
   * Key: 사용자 ID, Value: 읽은 시간.
   */
  seen: {
    [key: string]: string;
  } = {};
  /** Promise data */
  promise?: RocketchatPromiseType | undefined = undefined;
  /** System message type */
  system_message?: SystemMessage | undefined = undefined;
  /** The type of the template message. */
  template_type: TemplateType = TemplateType.TEMPLATE_TYPE_UNSPECIFIED;
  /** 보이는 상태를 조절하는 파라미터 */
  visible: Visible = Visible.VISIBLE_UNSPECIFIED;
  /** Sender의 id, type, nickname, profile_image 및 target_uri가 있다. */
  sender?: UserID;
  /** Receiver의 id, type이 있다. */
  receiver?: UserID;
  /**
   * 이미지 목록 (최대 길이: 10)
   * STIC, IMAG, GALL 타입 메시지에서 사용하는 필드
   */
  images: Image[] = [];
  /** Message가 삭제된 시간. 서버에서 생성한다. */
  delete_time?: string | undefined = undefined;
  /**
   * 일반 템플릿 (제목, 부제, 텍스트, 이미지 및 최대 세 개의 버튼이 포함된 구조화된 간단한 메시지)
   */
  generic_template?: Partial<GenericTemplate> | undefined = undefined;
  location_template?: Partial<LocationTemplate> | undefined = undefined;
  /** 답장하기 원글 */
  message_context?: MessageContext | undefined = undefined;
  /** 메시지에 찍혀있는 리액션 */
  reactions: ReactionSummary[] = [];
  /** 저장한 메시지인지 여부 */
  is_saved = false;
  /**
   * TODO: 추후 멘션을 위한 템플릿을 따로 만든다면 이 필드를 포함해야한다.
   * 멘션된 유저의 닉네임 정보, 비즈니스방의 경우는 고려하지 않는다.
   */
  mentioned_nickname_map: {
    [key: string]: string;
  } = {};
  /**
   * 클라이언트에서 만들어 사용하는 값. 서버에서 생성하지 않는다.
   * 메시지 전송에 실패한 경우 재전송을 위한 상태 값.
   */
  resendStatus: Values<typeof ResendStatus> = ResendStatus.UNSPECIFIED;
  /**
   * 클라이언트에서 만들어 사용하는 값. 서버에서 생성하지 않는다.
   * 이미지 같이 업로드 시간이 필요한 메시지의 미리 보기를 구현하기 위해 사용.
   */
  temporaryStatus: Values<typeof TemporaryStatus> =
    TemporaryStatus.NOT_TEMPORARY;

  static key = "ChatMessage";

  static schema = {
    receiver: User,
    sender: User,
  };

  static process(
    value: ChatMessage,
    parent: Entity,
    key: string,
    args: unknown[],
  ) {
    // biome-ignore lint/complexity/noThisInStatic: use super keyword
    const processed = super.process(value, parent, key, args);
    return {
      ...ChatMessage.defaults,
      ...processed,
      normalizeKey: getMessagePrimaryKey({
        message: {
          channelId: processed.channel_id,
          id: processed.id,
        },
      }),
    };
  }

  pk() {
    return this.normalizeKey;
  }
}
